import React from 'react';
import { ShieldCheck, Award, CheckCircle, Info, ExternalLink } from 'lucide-react';
import { Separator } from './ui/separator';

export function CertificationPage() {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="bg-brand-green text-white py-20 px-6 text-center">
        <div className="max-w-4xl mx-auto">
          <div className="flex justify-center mb-6 animate-in fade-in zoom-in duration-700">
            <ShieldCheck className="h-20 w-20 text-brand-gold" />
          </div>
          <h1 className="text-4xl sm:text-5xl font-serif mb-6 animate-in fade-in slide-in-from-bottom-4 duration-700">
            Certified <span className="text-brand-gold">Purity</span>
          </h1>
          <p className="text-lg text-brand-gold/80 max-w-2xl mx-auto animate-in fade-in slide-in-from-bottom-4 duration-700 delay-200">
            Your trust is our greatest asset. At RPK, every piece of jewellery is a testament to our commitment to absolute purity and quality.
          </p>
        </div>
      </section>

      {/* BIS Hallmarking Section */}
      <section className="py-20 px-6 max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div className="animate-in fade-in slide-in-from-left-8 duration-1000">
            <h2 className="text-3xl font-serif text-brand-green mb-6">BIS Hallmarked Gold</h2>
            <p className="text-muted-foreground leading-relaxed mb-6">
              All our gold jewellery carries the prestigious <strong>BIS (Bureau of Indian Standards)</strong> Hallmark. This is your guarantee of the purity and fineness of the gold you purchase. 
            </p>
            <div className="space-y-4">
              {[
                "BIS Logo - Authenticating the authority",
                "Purity in Karat & Fineness (e.g., 22K916)",
                "Assaying & Hallmarking Centre's Mark",
                "Jeweller's Identification Mark"
              ].map((point, i) => (
                <div key={i} className="flex items-center space-x-3">
                  <CheckCircle className="h-5 w-5 text-brand-green shrink-0" />
                  <span className="text-foreground">{point}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="bg-brand-gold/5 rounded-3xl p-12 border border-brand-gold/20 flex flex-col items-center animate-in fade-in zoom-in duration-1000">
            <div className="text-center mb-8">
              <p className="text-brand-green font-serif text-2xl mb-2">Purity Verification</p>
              <p className="text-sm text-muted-foreground uppercase tracking-widest">Always look for the hallmark</p>
            </div>
            <img 
              src="/bis-hallmark.png" 
              alt="BIS Hallmark" 
              className="w-full max-w-[450px] object-contain h-auto mx-auto drop-shadow-xl"
            />
          </div>
        </div>
      </section>

      {/* Why it Matters */}
      <section className="bg-off-white py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-serif text-brand-green mb-4">Why Certification Matters</h2>
            <Separator className="w-24 mx-auto bg-brand-gold h-1" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: Award,
                title: "Guaranteed Purity",
                desc: "Hallmarking ensures you get the exact gold karatage (18K, 22K, or 24K) promised."
              },
              {
                icon: Info,
                title: "Fair Value",
                desc: "Certified jewellery maintains its value during buy-backs or exchanges across India."
              },
              {
                icon: ShieldCheck,
                title: "Consumer Protection",
                desc: "Official certification protects you from under-caratage and fraudulent practices."
              }
            ].map((box, i) => (
              <div key={i} className="bg-white p-8 rounded-2xl shadow-sm border border-border hover:border-brand-green/20 transition-colors">
                <box.icon className="h-10 w-10 text-brand-green mb-4" />
                <h3 className="text-xl font-serif text-brand-green mb-2">{box.title}</h3>
                <p className="text-muted-foreground leading-relaxed">{box.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Silver Certification */}
      <section className="py-20 px-6 max-w-7xl mx-auto text-center">
        <h2 className="text-3xl font-serif text-brand-green mb-6">Silver Purity Assurance</h2>
        <p className="text-muted-foreground max-w-2xl mx-auto mb-10 leading-relaxed">
          Our silver articles and jewellery are crafted with high-quality sterling silver or fine silver, often marked with the <strong>925</strong> stamp (92.5% purity) or higher as per global standards.
        </p>
        <a 
          href="https://www.bis.gov.in/hallmarking/overview/" 
          target="_blank" 
          rel="noopener noreferrer"
          className="inline-flex items-center space-x-2 text-brand-green font-medium hover:underline"
        >
          <span>Learn more about BIS standards</span>
          <ExternalLink className="h-4 w-4" />
        </a>
      </section>
    </div>
  );
}
